import React from "react";
import { Formik } from "formik";
import * as yup from "yup";
import { Drawer, FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import { useDispatch } from "react-redux";
import IconButtonIcons from "../../Common/IconButtonIcons";
import Input from "../../Common/Input";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import { useCreateCouponMutation, useUpdateCouponMutation } from "../../../store/shopAdmin/shopAdminApis";

const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");
const emptyToNull = (v) => (v === "" || v === undefined ? null : v);

const validation = yup.object().shape({
    code: yup
        .string()
        .required("Code is required")
        .matches(/^[A-Za-z0-9_-]{3,30}$/, "3-30 letters, numbers, - or _"),
    discount_type: yup.string().oneOf(["percent", "flat"]).required(),
    discount_value: yup
        .number()
        .typeError("Enter a number")
        .positive("Must be more than 0")
        .required("Discount is required")
        .when("discount_type", { is: "percent", then: (s) => s.max(100, "Cannot exceed 100%") }),
    max_discount: yup.number().typeError("Enter a number").positive().nullable().transform((v, o) => (o === "" ? null : v)),
    min_cart_amount: yup.number().typeError("Enter a number").min(0).required(),
    usage_limit: yup.number().typeError("Enter a number").integer().min(1).nullable().transform((v, o) => (o === "" ? null : v)),
    per_customer_limit: yup.number().typeError("Enter a number").integer().min(1).required(),
    expires_at: yup
        .string()
        .nullable()
        .test("after-start", "Expiry must be after the start date", function (v) {
            const { starts_at } = this.parent;
            return !v || !starts_at || new Date(v) > new Date(starts_at);
        }),
});

const CouponDrawer = ({ open, coupon, onClose }) => {
    const dispatch = useDispatch();
    const [createCoupon, { isLoading: creating }] = useCreateCouponMutation();
    const [updateCoupon, { isLoading: updating }] = useUpdateCouponMutation();
    const isEdit = Boolean(coupon?.id);

    const onSubmit = async (values) => {
        const payload = {
            code: values.code.trim().toUpperCase(),
            description: values.description || null,
            discount_type: values.discount_type,
            discount_value: Number(values.discount_value),
            max_discount: values.discount_type === "percent" ? emptyToNull(values.max_discount) : null,
            min_cart_amount: Number(values.min_cart_amount || 0),
            first_order_only: values.first_order_only,
            usage_limit: emptyToNull(values.usage_limit),
            per_customer_limit: Number(values.per_customer_limit),
            starts_at: emptyToNull(values.starts_at),
            expires_at: values.expires_at ? `${values.expires_at}T23:59:59` : null,
            is_active: values.is_active,
        };
        try {
            if (isEdit) await updateCoupon({ id: coupon.id, ...payload }).unwrap();
            else await createCoupon(payload).unwrap();
            dispatch(setSnackBar({ open: true, message: isEdit ? "Coupon updated" : "Coupon created", severity: "success" }));
            onClose();
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not save coupon", severity: "error" }));
        }
    };

    return (
        <Formik
            enableReinitialize
            validationSchema={validation}
            onSubmit={onSubmit}
            initialValues={{
                code: coupon?.code || "",
                description: coupon?.description || "",
                discount_type: coupon?.discount_type || "percent",
                discount_value: coupon?.discount_value ?? "",
                max_discount: coupon?.max_discount ?? "",
                min_cart_amount: coupon?.min_cart_amount ?? 0,
                first_order_only: coupon?.first_order_only ?? false,
                usage_limit: coupon?.usage_limit ?? "",
                per_customer_limit: coupon?.per_customer_limit ?? 1,
                starts_at: toDateInput(coupon?.starts_at),
                expires_at: toDateInput(coupon?.expires_at),
                is_active: coupon?.is_active ?? true,
            }}
        >
            {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
                <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: "34%" } } }}>
                    <Grid container direction="column" height="100%">
                        <Grid container flex={0} px={2} py={1} borderBottom={1} borderColor="rgba(5, 5, 5, 0.06)" justifyContent="space-between">
                            <Grid item alignSelf="center">
                                <Typography variant="h6">{isEdit ? "Update Coupon" : "Create Coupon"}</Typography>
                            </Grid>
                            <Grid item alignSelf="center">
                                <IconButtonIcons color="default" title="Close" IconComponent={CloseIcon} onClick={onClose} />
                            </Grid>
                        </Grid>
                        <Grid flex={1} px={2} py={2} overflow="auto">
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Input
                                        label="Coupon code"
                                        name="code"
                                        value={values.code}
                                        onChange={(e) => setFieldValue("code", e.target.value.toUpperCase())}
                                        error={Boolean(errors.code)}
                                        helperText={errors.code}
                                        fullWidth
                                        required
                                        placeholder="e.g. DIWALI10"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Input label="Description (shown to admins)" name="description" value={values.description} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField select fullWidth label="Discount type" name="discount_type" value={values.discount_type} onChange={handleChange} sx={{ mt: 3 }}>
                                        <MenuItem value="percent">Percent (%)</MenuItem>
                                        <MenuItem value="flat">Flat amount (₹)</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <Input
                                        label={values.discount_type === "percent" ? "Discount %" : "Discount ₹"}
                                        name="discount_value"
                                        type="number"
                                        value={values.discount_value}
                                        onChange={handleChange}
                                        error={Boolean(errors.discount_value)}
                                        helperText={errors.discount_value}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                {values.discount_type === "percent" ? (
                                    <Grid item xs={12}>
                                        <Input
                                            label="Maximum discount ₹ (optional cap)"
                                            name="max_discount"
                                            type="number"
                                            value={values.max_discount}
                                            onChange={handleChange}
                                            error={Boolean(errors.max_discount)}
                                            helperText={errors.max_discount}
                                            fullWidth
                                        />
                                    </Grid>
                                ) : null}
                                <Grid item xs={12}>
                                    <Input
                                        label="Minimum cart amount ₹"
                                        name="min_cart_amount"
                                        type="number"
                                        value={values.min_cart_amount}
                                        onChange={handleChange}
                                        error={Boolean(errors.min_cart_amount)}
                                        helperText={errors.min_cart_amount}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Input
                                        label="Total uses (blank = unlimited)"
                                        name="usage_limit"
                                        type="number"
                                        value={values.usage_limit}
                                        onChange={handleChange}
                                        error={Boolean(errors.usage_limit)}
                                        helperText={errors.usage_limit}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Input
                                        label="Uses per customer"
                                        name="per_customer_limit"
                                        type="number"
                                        value={values.per_customer_limit}
                                        onChange={handleChange}
                                        error={Boolean(errors.per_customer_limit)}
                                        helperText={errors.per_customer_limit}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Input label="Starts on (optional)" name="starts_at" type="date" value={values.starts_at} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={6}>
                                    <Input
                                        label="Expires on (optional)"
                                        name="expires_at"
                                        type="date"
                                        value={values.expires_at}
                                        onChange={handleChange}
                                        error={Boolean(errors.expires_at)}
                                        helperText={errors.expires_at}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={values.first_order_only} onChange={(e) => setFieldValue("first_order_only", e.target.checked)} />}
                                        label="Only for a customer's first order"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={values.is_active} onChange={(e) => setFieldValue("is_active", e.target.checked)} />}
                                        label="Active"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid flex={0} px={2} py={2} borderTop={1} borderColor="rgba(5, 5, 5, 0.06)">
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Button variant="outlined" fullWidth onClick={onClose}>
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button variant="contained" fullWidth onClick={handleSubmit} loading={creating || updating}>
                                        {isEdit ? "Update" : "Create"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Drawer>
            )}
        </Formik>
    );
};

export default CouponDrawer;
